// UserStatusIndicator.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import LiveStreamBadge from '../container/LiveStreamBadge.js';
import {
  LIVESTREAM_TYPE_DOT,
  ONLINE_TYPE_DOT,
  ONLINE_TYPE_PROFILE_DOT,
  ONLINE_TYPE_PROFILE_MOBILE_DOT,
  ONLINE_TYPE_BIGGER_DOT,
  ONLINE_TYPE_BIGGER_DOT_WITH_BLACK_BORDER,
} from '../resource/userStatusIndicatorConstants.js';
import { color } from '../style/variables';

export class UserStatusIndicator extends React.PureComponent {
  subscribeOnlineStatus = () => {
    const {
      userId,
      subscribeOnlineStatus,
      shouldShowLiveStream,
      shouldShowOnline,
      shouldShowOffline,
    } = this.props;

    if (!userId) {
      return;
    }

    const shouldSubscribeChannel =
      shouldShowLiveStream || shouldShowOnline || shouldShowOffline;
    if (shouldSubscribeChannel) subscribeOnlineStatus({ userId });
  };
  componentDidMount() {
    this.subscribeOnlineStatus();
  }
  componentDidUpdate(prevProps) {
    const { userId: prevUserId } = prevProps;
    const { userId } = this.props;

    if (userId !== prevUserId) {
      this.subscribeOnlineStatus();
    }
  }
  render() {
    const {
      userId,
      isLiveStreaming,
      isOnline,
      shouldShowLiveStream,
      liveStreamType,
      livestreamSize,
      onlineType,
      shouldShowOnline,
      shouldShowOffline,
    } = this.props;

    if (!userId) {
      return null;
    }

    if (isLiveStreaming && shouldShowLiveStream) {
      return (
        <LiveStreamBadge
          streamId={userId}
          userId={userId}
          type={liveStreamType}
          size={livestreamSize}
        />
      );
    }

    if (shouldShowOnline && isOnline) {
      return <OnlineIndicator type={onlineType} />;
    }

    if (shouldShowOffline && !isOnline) {
      return <OfflineIndicator type={onlineType} />;
    }

    return null;
  }
}

UserStatusIndicator.propTypes = {
  userId: PropTypes.string,
  liveStreamType: PropTypes.string,
  livestreamSize: PropTypes.number,
  onlineType: PropTypes.string,
  isLiveStreaming: PropTypes.bool,
  isOnline: PropTypes.bool,
  shouldShowLiveStream: PropTypes.bool,
  shouldShowOnline: PropTypes.bool,
  shouldShowOffline: PropTypes.bool,
  subscribeOnlineStatus: PropTypes.func,
};

UserStatusIndicator.defaultProps = {
  userId: null,
  liveStreamType: LIVESTREAM_TYPE_DOT,
  livestreamSize: 18,
  onlineType: ONLINE_TYPE_DOT,
  isLiveStreaming: null,
  isOnline: false,
  shouldShowLiveStream: false,
  shouldShowOnline: false,
  shouldShowOffline: false,
  subscribeOnlineStatus: () => null,
};

const getDimension = type => {
  switch (type) {
    case ONLINE_TYPE_PROFILE_DOT:
      return '20px';
    case ONLINE_TYPE_PROFILE_MOBILE_DOT:
      return '16px';
    case ONLINE_TYPE_BIGGER_DOT:
    case ONLINE_TYPE_BIGGER_DOT_WITH_BLACK_BORDER:
      return '12px';
    default:
      return '8px';
  }
};

const getMargin = type => {
  switch (type) {
    case ONLINE_TYPE_BIGGER_DOT:
    case ONLINE_TYPE_BIGGER_DOT_WITH_BLACK_BORDER:
      return '2px';
    case ONLINE_TYPE_PROFILE_DOT:
      return '4px';
    default:
      return '';
  }
};

const OnlineIndicator = styled.div.attrs(({ type }) => ({
  style: {
    width: getDimension(type),
    height: getDimension(type),
    margin: getMargin(type),
  },
}))`
  border-radius: 50%;
  background-color: ${color.lime};
  ${({ type }) => {
    if (type === ONLINE_TYPE_BIGGER_DOT_WITH_BLACK_BORDER) {
      return css`
        border: 1px solid ${color.grey['900']};
      `;
    }
  }}
`;

const OfflineIndicator = styled.div.attrs(({ type }) => ({
  style: {
    width: type === ONLINE_TYPE_BIGGER_DOT ? '12px' : '8px',
    height: type === ONLINE_TYPE_BIGGER_DOT ? '12px' : '8px',
    margin: type === ONLINE_TYPE_BIGGER_DOT ? '' : '2px',
  },
}))`
  border-radius: 50%;
  background-color: ${color.grey['300']};
`;

export default UserStatusIndicator;
